import { useEffect } from 'react';
import { asIdentityId } from 'stores/sessionReadiness';
import { getSessionIdentity } from 'stores/sessionIdentity';
import { useSupportUserStore } from 'stores/supportUserStore';
import { useSessionStore } from 'stores/sessionStore';
import { StorageKey, storage } from 'utils/typedStorage';
import { paths, type useAppNavigate } from 'url-registry';

type AppNavigate = ReturnType<typeof useAppNavigate>;

// A logout clears the whole store, which arrives as a null key.
const WATCHED_KEYS: string[] = [
  StorageKey.AuthToken,
  StorageKey.UserId,
  StorageKey.OrgId,
];

/**
 * Keeps this tab in step with auth changes made in another tab: a logout sends
 * this tab to /login, and a new token or a different identity reloads it so no
 * screen keeps rendering the previous session's data.
 */
export const useCrossTabAuthSync = (navigate: AppNavigate): void => {
  useEffect(() => {
    let authToken = storage.get(StorageKey.AuthToken);

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== null && !WATCHED_KEYS.includes(event.key)) {
        return;
      }
      const newAuthToken = storage.get(StorageKey.AuthToken);
      if (authToken !== newAuthToken) {
        // A replaced or ended auth session must not inherit this tab's support display choice.
        useSupportUserStore.getState().setUiLanguageOverride(null);
        authToken = newAuthToken;
        if (!newAuthToken) {
          try {
            useSessionStore.getState().invalidateLocalSession();
          } finally {
            // Even a failed scoped cleanup installs signed-out state first.
            navigate(paths.LOGIN);
          }
        } else {
          window.location.reload();
        }
        return;
      }
      const incomingUserId = asIdentityId(
        event.key === StorageKey.UserId
          ? event.newValue
          : storage.get(StorageKey.UserId)
      );
      const incomingOrgId = asIdentityId(
        event.key === StorageKey.OrgId
          ? event.newValue
          : storage.get(StorageKey.OrgId)
      );
      const session = getSessionIdentity();
      if (
        session.userId !== incomingUserId ||
        session.orgId !== incomingOrgId
      ) {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);
};
